// Central data-testid registry for Fursa homepage (Sprint 1).
// All interactive & user-facing elements must have unique IDs.

export const HOME = {
  emergentLink: "home-emergent-link",
};

export const HEADER = {
  root: "site-header",
  logo: "header-logo",
  navHome: "nav-link-home",
  navOpportunities: "nav-link-opportunities",
  navSectors: "nav-link-sectors",
  navCountries: "nav-link-countries",
  navAbout: "nav-link-about",
  mobileMenuButton: "header-mobile-menu-button",
  mobileMenuPanel: "header-mobile-menu-panel",
  ctaEnter: "header-cta-enter",
  settings: "header-settings",
};

export const HERO = {
  root: "hero-section",
  title: "hero-title",
  subtitle: "hero-subtitle",
  primaryCta: "hero-primary-cta",
  eyebrow: "hero-eyebrow",
  metric: (key) => `hero-metric-${key}`,
};

export const SEARCH_SECTION = {
  root: "home-search-section",
  input: "home-search-input",
  button: "home-search-button",
};

export const HOME_QUICK_FILTERS = {
  root: "home-quick-filters",
  pill: (key) => `home-quick-filter-${key}`,
};

export const FEATURED = {
  root: "featured-opportunity",
  title: "featured-opportunity-title",
  score: "featured-opportunity-score",
  readMore: "featured-opportunity-read-more",
};

export const LATEST = {
  root: "latest-opportunities",
  card: (id) => `opportunity-card-${id}`,
  cardTitle: (id) => `opportunity-card-${id}-title`,
  cardReadMore: (id) => `opportunity-card-${id}-read-more`,
  viewAll: "latest-opportunities-view-all",
};

export const CATEGORIES = {
  root: "categories-section",
  item: (slug) => `category-${slug}`,
};

export const INDUSTRIES = {
  root: "industries-section",
  item: (slug) => `industry-${slug}`,
};

export const COUNTRIES = {
  root: "countries-section",
  item: (slug) => `country-${slug}`,
};

export const FOOTER = {
  root: "site-footer",
  linkPrivacy: "footer-link-privacy",
  linkTerms: "footer-link-terms",
  linkContact: "footer-link-contact",
};

// ---------------------------------------------------------------------------
// Sprint 2: Opportunities Listing + Opportunity Details
// ---------------------------------------------------------------------------

export const LIST_PAGE = {
  root: "opportunities-list-page",
  title: "list-page-title",
  searchInput: "list-search-input",
  filterType: "list-filter-type",
  filterCountry: "list-filter-country",
  filterIndustry: "list-filter-industry",
  filterCapital: "filter-capital",
  filterScore: "filter-score",
  filterTime: "filter-time",
  filterRecency: "filter-recency",
  filterRisk: "filter-risk",
  filterStatus: "filter-status",
  filterLabel: "filter-label",
  filterGroup: (key) => `filter-group-${key}`,
  filterSort: "list-filter-sort",
  clearFilters: "list-clear-filters",
  filterPanel: "list-filter-panel",
  mobileFiltersTrigger: "mobile-filters-trigger",
  loadingSkeleton: "list-loading-skeleton",
  loadMore: "opportunities-load-more",
  activeChips: "active-filter-chips",
  clearAllChips: "clear-all-chips",
  error: "list-error-state",
  errorRetry: "opportunities-error-retry",
  emptyReset: "empty-reset-btn",
  resultsCount: "list-results-count",
  grid: "list-grid",
  empty: "list-empty-state",
  activeChip: (key) => `active-chip-${key}`,
};

export const DETAIL_PAGE = {
  root: "opportunity-detail-page",
  loading: "detail-loading",
  error: "detail-error",
  errorRetry: "detail-error-retry",
  breadcrumbHome: "detail-breadcrumb-home",
  breadcrumbList: "detail-breadcrumb-list",
  title: "detail-title",
  score: "detail-score",
  type: "detail-type",
  country: "detail-country",
  industry: "detail-industry",
  publishedAt: "detail-published-at",
  summary: "detail-summary",
  executiveSummary: "detail-executive-summary",
  keyIndicators: "detail-key-indicators",
  keyIndicator: (i) => `detail-key-indicator-${i}`,
  suggestedSteps: "detail-suggested-steps",
  sources: "detail-sources",
  tags: "detail-tags",
  related: "detail-related-section",
  ctaSave: "detail-cta-save",
  ctaShare: "detail-cta-share",
  ctaContact: "detail-cta-contact",
  ctaCopyLink: "detail-cta-copy-link",
  backButton: "detail-back-button",
  stickyMobileCta: "detail-sticky-mobile-cta",
  backToList: "detail-back-to-list",
  notFound: "detail-not-found",
};

// Bottom navigation (mobile)
export const BOTTOM_NAV = {
  root: "bottom-nav",
  home: "bottom-nav-home",
  opportunities: "bottom-nav-opportunities",
  knowledge: "bottom-nav-knowledge",
  saved: "bottom-nav-saved",
  about: "bottom-nav-about",
};

// Reusable back button
export const BACK_BUTTON = {
  root: "back-button",
};

// About page
export const ABOUT = {
  root: "about-page",
  title: "about-title",
  description: "about-description",
  version: "about-version",
  contact: "about-contact",
  privacy: "about-privacy",
  terms: "about-terms",
  backButton: "about-back-button",
};

// Settings page
export const SETTINGS = {
  root: "settings-page",
  title: "settings-title",
  rowAbout: "settings-row-about",
  rowPrivacy: "settings-row-privacy",
  rowTerms: "settings-row-terms",
  rowContact: "settings-row-contact",
  rowShare: "settings-row-share",
  rowRate: "settings-row-rate",
  backButton: "settings-back-button",
};

// Error / loading UI
export const ERROR_UI = {
  boundary: "error-boundary-view",
  retry: "error-retry-button",
  offlineBanner: "offline-banner",
};

// ---------------------------------------------------------------------------
// Sprint 3 — Admin Dashboard
// ---------------------------------------------------------------------------

export const ADMIN = {
  root:               "admin-root",
  sidebar:            "admin-sidebar",
  topbar:             "admin-topbar",
  pageTitle:          "admin-page-title",
  mobileMenuBtn:      "admin-mobile-menu-btn",
  backToSite:         "admin-back-to-site",
  navItem:            (key) => `admin-nav-${key}`,

  // Dashboard
  kpiGrid:            "admin-kpi-grid",
  kpiPending:         "admin-kpi-pending",
  kpiPublished:       "admin-kpi-published",
  kpiRejected:        "admin-kpi-rejected",
  kpiSources:         "admin-kpi-sources",
  activityTitle:      "admin-activity-title",
  activityList:       "admin-activity-list",
  activityItem:       (id) => `admin-activity-item-${id}`,

  // Review
  reviewTabs:         "admin-review-tabs",
  reviewTab:          (key) => `admin-review-tab-${key}`,
  reviewTable:        "admin-review-table",
  reviewRow:          (id) => `admin-review-row-${id}`,
  reviewCard:         (id) => `admin-review-card-${id}`,
  reviewRefresh:      "admin-review-refresh",
  reviewSearch:       "admin-review-search",
  reviewEmpty:        "review-empty-state",
  reviewError:        "review-error-state",
  reviewErrorRetry:   "review-error-retry",
  reviewLoading:      "review-loading",
  actionPublish:      (id) => `admin-action-publish-${id}`,
  actionEdit:         (id) => `admin-action-edit-${id}`,
  actionReject:       (id) => `admin-action-reject-${id}`,

  // Editor
  editorRoot:         "admin-editor-root",
  editorNotFound:     "admin-editor-not-found",
  editorField:        (key) => `admin-editor-field-${key}`,
  editorSave:         "admin-editor-save",
  editorPublish:      "admin-editor-publish",

  // Sources
  sourcesTable:       "admin-sources-table",
  sourceRow:          (id) => `admin-source-row-${id}`,

  // Feed Monitor
  feedTable:          "admin-feed-table",
  feedRow:            (id) => `admin-feed-row-${id}`,
  feedFetchBtn:       (id) => `admin-feed-fetch-${id}`,
  feedFetchAllBtn:    "admin-feed-fetch-all",

  // AI Queue
  aiQueueList:        "admin-ai-queue-list",
  aiQueueItem:        (id) => `admin-ai-queue-item-${id}`,
  aiAnalyzeBtn:       (id) => `admin-ai-analyze-${id}`,
  aiReanalyzeBtn:     (id) => `admin-ai-reanalyze-${id}`,
  aiArchiveBtn:       (id) => `admin-ai-archive-${id}`,
  aiResultCard:       (id) => `admin-ai-result-${id}`,
  aiRejectionCard:    (id) => `admin-ai-rejection-${id}`,
  aiFailureCard:      (id) => `admin-ai-failure-${id}`,
  aiStageIndicator:   (id) => `admin-ai-stage-${id}`,
  aiSendToReviewBtn:  (id) => `admin-ai-send-to-review-${id}`,
  // Sprint 6 — evidence-aware
  aiSignalBadge:      (id) => `admin-ai-signal-${id}`,
  aiOmiBadge:         (id) => `admin-ai-omi-${id}`,
  aiEvidenceComp:     (id) => `admin-ai-evidence-comp-${id}`,
  aiConflictsSection: (id) => `admin-ai-conflicts-${id}`,
  aiDecisionLog:      (id) => `admin-ai-decision-log-${id}`,
  aiFieldEvidence:    (id) => `admin-ai-field-evidence-${id}`,
  aiPublishBtn:       (id) => `admin-ai-publish-${id}`,
  aiEditBtn:          (id) => `admin-ai-edit-${id}`,

  // Settings
  settingsPlaceholder: "admin-settings-placeholder",
};
